<template>
  <div class="sp-modal" :class="[screenModifiers, positionModifier]">
    <slot name="activator" @click.stop="handleActivatorClick" />

    <component
      :is="surfaceComponent"
      v-bind="$props"
      :open="open"
      no-scrollable
      :width="width"
      :is-hidden-temporarily="isHiddenTemporarily"
      :no-content-padding="noMobileContentPadding"
      :expand-to-full-height="expandToFullHeight"
      :expand-height-max="expandHeightMax"
      @close="handleDialogClose"
      @update-open="handleDialogUpdateOpen"
    >
      <sp-card
        class="sp-modal__card"
        :class="cardModifiers"
        variant="flat"
        :subtitle="subtitle"
        :scrollable-content="contentScrollable"
        grid
      >
        <div slot="title" class="sp-modal__card-header">
          <slot name="header">
            <div class="sp-modal__card-title">
              <slot name="title">
                <sp-card-title v-if="modalTitle" :oneline="titleOneline">
                  {{ modalTitle }}
                </sp-card-title>
              </slot>
            </div>
          </slot>

          <div v-if="showCloseButton" class="sp-modal__close-container" aria-hidden="false">
            <sp-button
              icon="close-bold"
              :label="closeLabel"
              :size="closeButtonSize"
              class="close-button"
              color="text"
              variant="text"
              :data-testid="closeTestId"
              @click="handleCloseButtonClick"
            >
            </sp-button>
          </div>
        </div>

        <div slot="content-overlay">
          <slot name="content-overlay" />
        </div>

        <div slot="subtitle">
          <slot name="subtitle" />
        </div>

        <sp-card-content ref="content">
          <div class="sp-modal-alert-container">
            <div class="sp-modal__alert">
              <slot name="alert" />
            </div>
          </div>

          <slot />
        </sp-card-content>

        <sp-card-actions slot="actions" class="sp-modal__actions">
          <slot name="actions" />
        </sp-card-actions>
      </sp-card>
    </component>
  </div>
</template>

<script setup>
/**
 * The `sp-modal` component is a wrapper around sp-dialog that provides a default layout for the dialog content.
 * It includes a header with a title and a close button, a subtitle, and a slot for the main content.
 * It also provides a slot for the actions that will be displayed at the bottom of the dialog.
 * It also provides a slot for the activator element that will open the dialog when clicked.
 *
 * @slot activator - The element that will open the dialog when clicked.
 * @slot header - Overrides the title area only (default close button may still render).
 * @slot title - Overrides the default `modalTitle` rendering.
 * @slot subtitle - Overrides the subtitle slot content.
 * @slot actions - The actions that will be displayed at the bottom of the dialog.
 *
 * @emits close - Emitted when the dialog is closed.
 * @emits update-open - Emitted when the open property is updated.
 */
import { computed, useTemplateRef } from "vue";
import { tAccessibleName } from "#ce/shared/components-i18n";
import { useDialogBridge } from "#ce/composables/useDialogBridge.js";
import { useExpose } from "#ce/composables/useExpose.js";
import { toBoolean } from "#ce/utils/props";
import { props as modalProps } from "./props.js";
import { useResponsiveSurface } from "#ce/composables/useResponsiveSurface.js";
import { useModalCloseButton } from "./composables/useModalCloseButton.js";

const emit = defineEmits(["close", "update-open"]);

const props = defineProps(modalProps);

// sp-modal emits `close` only when the inner surface fires its own `close`
// (via handleDialogClose), not on a bare update-open(false), so the bridge gets
// no onClose hook. The inner sp-dialog defers its first showModal to its own
// onMounted gate, so the open prop passes straight through with no deferral here.
const { open, handleDialogUpdateOpen } = useDialogBridge(props, emit);
const { surfaceComponent, screenModifiers } = useResponsiveSurface(props);
const { closeButtonSize, showCloseButton } = useModalCloseButton(props);

const titleOneline = computed(() => toBoolean(props.titleOneline));
const contentScrollable = computed(() => !toBoolean(props.noScrollableContent));
const cardModifiers = computed(() => ({ "--no-scrollable-content": !contentScrollable.value }));
const positionModifier = computed(() => `--position-${props.position}`);

// sp-button turns `label` into the icon-only button's `aria-label`, not into text.
const closeLabel = computed(() => tAccessibleName("modal_close"));

const content = useTemplateRef("content");

const { exposeMethods } = useExpose();

exposeMethods({
  scrollContentIntoView,
});
/**
 * Scrolls the content into view.
 * The behavior of this function can be customized by passing an options object.
 * For more information, see the MDN documentation: https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollIntoView
 * @param options {ScrollIntoViewOptions}
 */
function scrollContentIntoView(options) {
  content.value.scrollIntoView(options);
}

function handleActivatorClick() {
  open.value = true;
}

function handleCloseButtonClick() {
  open.value = false;
}

function handleDialogClose() {
  emit("close");
  open.value = false;
}
</script>

<style>
:host {
  display: block;
  --modal-header-background-color: var(--sp-comp-modal-header-background-color, var(--sp-sys-color-primary));
  --modal-header-color: var(--sp-comp-modal-header-color, var(--sp-sys-color-on-primary));
  --modal-padding: var(--sp-comp-modal-padding, calc(var(--sp-ref-scale-16) * 1.5));
  --modal-card-padding-inline: var(--modal-padding);
  /* Read on the host, so a `--sp-comp-card-padding-block` set outside the modal still reaches the card while
     `--sp-comp-modal-padding-block` is unset. */
  --modal-card-padding-block: var(--sp-comp-modal-padding-block, var(--sp-comp-card-padding-block));
  --modal-actions-padding-block: var(--sp-comp-modal-actions-padding-block, var(--sp-card-actions-padding-block));

  /* Close container */
  --close-container-justify-content: var(--sp-comp-modal-close-container-justify-content, flex-end);
  --close-container-align-items: var(--sp-comp-modal-close-container-align-items, center);
  --close-container-width: var(--sp-comp-modal-close-container-width, var(--sp-comp-modal-close-container-size, auto));
  --close-container-height: var(
    --sp-comp-modal-close-container-height,
    var(--sp-comp-modal-close-container-size, auto)
  );
  --close-container-position: var(--sp-comp-modal-close-container-position, static);
  --close-container-inset-block-start: var(--sp-comp-modal-close-container-inset-block-start, auto);
  --close-container-inset-inline-end: var(--sp-comp-modal-close-container-inset-inline-end, auto);
  --close-container-z-index: var(--sp-comp-modal-close-container-z-index, auto);
  --close-container-padding-top: var(--sp-comp-modal-close-container-padding-top, 0);

  /* The inner sp-card owns content padding; avoid double padding when sp-modal routes through sp-bottom-sheet on mobile. */
  --sp-comp-bottom-sheet-content-padding-inline: 0;
  --sp-comp-bottom-sheet-content-padding-block: 0;
}
</style>

<style scoped lang="scss">
.sp-modal {
  // The card sizes to its content and caps at a viewport-relative max-height: a
  // tall body scrolls within the cap with the header and actions pinned. Set
  // --sp-comp-modal-card-height / --sp-comp-modal-card-max-height to bound it.
  --card-height: var(--sp-comp-modal-card-height, auto);
  --card-max-height: var(--sp-comp-modal-card-max-height, 90vh);
  --card-title-min-height: var(--sp-comp-modal-header-min-height, calc(var(--sp-ref-scale-16, 1rem) * 4));
  --card-header-align-items: var(--sp-comp-modal-header-align-items, center);
  --sp-comp-dialog-backdrop-background-color: var(--sp-comp-modal-backdrop-background-color, #000);
  --sp-comp-dialog-backdrop-opacity: var(--sp-comp-modal-backdrop-opacity, 0.3);
}
// A drawer's dialog already owns the viewport sizing, so the card tracks that
// height instead of its own viewport cap.
.sp-modal.--position-left,
.sp-modal.--position-right {
  --card-height: var(--sp-comp-modal-card-height, 100%);
  --card-max-height: var(--sp-comp-modal-card-max-height, 100%);
}

.sp-modal__card {
  // The dialog or bottom sheet clips the card to its own corners, so the card is square by default.
  --sp-comp-card-border-radius: var(--sp-comp-modal-card-border-radius, 0);
  --sp-comp-card-height: var(--card-height);
  --sp-comp-card-max-height: var(--card-max-height);
  --sp-card-root-height: var(--card-height);
  --sp-comp-card-padding-inline: var(--modal-card-padding-inline);
  --sp-comp-card-padding-block: var(--modal-card-padding-block);
}

// The slotted content owns its scrolling, so the card content clips rather than
// becoming a second scroll container around it.
.sp-modal__card.--no-scrollable-content {
  --sp-comp-card-content-overflow-y: clip;
}

// On the bottom-sheet surface the sheet owns the viewport sizing — its cap is
// computed from the visible viewport, not vh — so a vh-based card cap can end
// short of the sheet and leave a band of sheet background below the card.
// Track the sheet's height (inherited through the sheet's shadow tree) instead.
sp-bottom-sheet .sp-modal__card {
  --card-max-height: var(--sp-comp-modal-card-max-height, var(--bottom-sheet-max-height, 90vh));
}

.sp-modal__card-header {
  box-sizing: border-box;
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: var(--card-header-align-items);
  position: relative;
  background-color: var(--modal-header-background-color);
  color: var(--modal-header-color);
  padding: var(--sp-comp-modal-header-padding-block, var(--sp-ref-scale-8, 0.5rem))
    var(--sp-comp-modal-header-padding-inline, var(--modal-padding));
  border-bottom: var(--sp-comp-modal-header-border-bottom, none);
  min-height: var(--card-title-min-height);
}

.sp-modal__close-container {
  display: flex;
  justify-content: var(--close-container-justify-content);
  align-items: var(--close-container-align-items);
  align-self: stretch;
  width: var(--close-container-width);
  height: var(--close-container-height);

  position: var(--close-container-position);
  inset-block-start: var(--close-container-inset-block-start);
  inset-inline-end: var(--close-container-inset-inline-end);
  z-index: var(--close-container-z-index);
  padding-top: var(--close-container-padding-top);
}

.sp-modal__card-title {
  background-color: transparent;
  padding: 0;
  border-bottom: none;
  min-height: 0;
  display: grid;
  align-items: center;
  height: 100%;
  font-size: var(--sp-comp-modal-header-font-size, 1.125rem);
  font-weight: var(--sp-comp-modal-header-font-weight, var(--sp-sys-font-weight-bold));
  font-family: var(--sp-comp-modal-header-font-family, var(--sp-sys-font-family-bold));
}

.close-button {
  --sp-comp-button-bg-color: var(--sp-comp-modal-close-button-bg-color, var(--modal-header-background-color));
  transform: translateX(var(--sp-comp-modal-close-button-icon-offset, var(--sp-ref-scale-8, 0.5rem)));
}

.close-button::part(icon) {
  --fill: var(--sp-comp-modal-close-button-icon-fill, var(--modal-header-color));
}

.close-button:focus {
  --sp-comp-button-focus-opacity: 0;
}
.sp-modal-alert-container {
  position: sticky;
  top: var(--sp-comp-modal-alert-container-top, var(--modal-padding));

  width: 100%;
  z-index: var(--sp-comp-modal-alert-z-index, var(--sp-sys-z-index-max, 99));

  .sp-modal__alert {
    position: absolute;
    width: 100%;
  }
}

// Modal actions align to the trailing edge by default, overridable per modal.
.sp-modal__actions {
  --sp-comp-card-actions-justify-content: var(--sp-comp-modal-actions-justify-content, flex-end);
  --sp-card-actions-padding-block: var(--modal-actions-padding-block);
}
</style>
